import { useId, useMemo, useState } from "react";
import {
  haversineMiles,
  QC_CENTER,
  QC_TOWNS,
  QC_VIEW_BOUNDS,
  townKey,
  townKeysWithinRadius,
  type ServiceTown,
} from "./qcServiceArea.js";
// Baked Web-Mercator backdrop, cropped to QC_VIEW_BOUNDS so it registers with
// the projected dots. Regenerate with scripts/build-qc-basemap.py after any
// QC_TOWNS change — do not hand-align. (c) OpenStreetMap, (c) CARTO.
import qcBasemap from "./qc-basemap.webp";

/**
 * Service-area picker (build-plan: service-area map). The contractor brushes
 * a radius around the Quad Cities core to bulk-select towns, then taps to
 * add or remove — the tap is where "I'll go into the QC but not into the
 * country" gets expressed. Selection is a set of town keys; the caller
 * expands it to the canonical ZIP set.
 *
 * Two synced surfaces: an SVG coverage map (radius circle + geographically
 * placed dots) for spatial feedback, and a grouped town list that is the
 * reliable tap target — the downtown towns cluster too tightly for bare map
 * dots to be hittable on a phone. Tapping either updates both.
 */

const W = 320;
const H = 250;
const PAD = 18;
const MILES_PER_DEG_LAT = 69;

const { minLat, maxLat, minLng, maxLng } = QC_VIEW_BOUNDS;
const LAT_MID_RAD = (((minLat + maxLat) / 2) * Math.PI) / 180;
const lngUnits = (maxLng - minLng) * Math.cos(LAT_MID_RAD); // east-west, degrees*cos
const latUnits = maxLat - minLat; // north-south, degrees
const SCALE = Math.min((W - 2 * PAD) / lngUnits, (H - 2 * PAD) / latUnits);
const OFF_X = (W - lngUnits * SCALE) / 2;
const OFF_Y = (H - latUnits * SCALE) / 2;
// The basemap image occupies exactly the projected bounds rect — same origin
// and span the dots are placed within, so it stays glued to them.
const MAP_W = lngUnits * SCALE;
const MAP_H = latUnits * SCALE;

function project(lat: number, lng: number): { x: number; y: number } {
  const east = (lng - minLng) * Math.cos(LAT_MID_RAD);
  const north = maxLat - lat;
  return { x: OFF_X + east * SCALE, y: OFF_Y + north * SCALE };
}

const DEFAULT_RADIUS_MI = 15;
const MIN_RADIUS_MI = 5;
const MAX_RADIUS_MI = 40;

export function ServiceAreaMap({
  selected,
  onChange,
}: {
  selected: readonly string[];
  onChange: (townKeys: string[]) => void;
}) {
  const [radius, setRadius] = useState(DEFAULT_RADIUS_MI);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const clipId = useId();

  const center = project(QC_CENTER.lat, QC_CENTER.lng);
  const radiusPx = (radius / MILES_PER_DEG_LAT) * SCALE;

  function applyRadius(mi: number) {
    setRadius(mi);
    onChange(townKeysWithinRadius(QC_CENTER.lat, QC_CENTER.lng, mi));
  }

  function toggle(key: string) {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next]);
  }

  const iowa = QC_TOWNS.filter((t) => t.state === "IA");
  const illinois = QC_TOWNS.filter((t) => t.state === "IL");

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="block text-sm font-medium text-charcoal">Service area</span>
        <span className="text-xs text-charcoal">{selectedSet.size} towns selected</span>
      </div>

      <label className="mt-2 block">
        <span className="text-xs text-charcoal">
          Quick select: towns within{" "}
          <span className="font-semibold text-brand-700">{radius} miles</span> of the Quad Cities
        </span>
        <input
          type="range"
          min={MIN_RADIUS_MI}
          max={MAX_RADIUS_MI}
          step={5}
          value={radius}
          onChange={(e) => applyRadius(Number(e.target.value))}
          aria-label="Service radius in miles"
          className="mt-1 w-full accent-brand-700"
        />
        <span className="text-xs text-charcoal">
          Drag to bulk-select, then add or remove towns below.
        </span>
      </label>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 w-full rounded-xl border border-charcoal/15 bg-field-gray"
        role="img"
        aria-label="Map of Quad Cities area towns; selected towns are filled"
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={OFF_X} y={OFF_Y} width={MAP_W} height={MAP_H} rx={10} ry={10} />
          </clipPath>
        </defs>
        <image
          href={qcBasemap}
          x={OFF_X}
          y={OFF_Y}
          width={MAP_W}
          height={MAP_H}
          preserveAspectRatio="none"
          clipPath={`url(#${clipId})`}
          aria-hidden="true"
        />
        <circle
          cx={center.x}
          cy={center.y}
          r={radiusPx}
          className="fill-brand-500/15 stroke-brand-600/50"
          strokeWidth={1}
        />
        {QC_TOWNS.map((t) => {
          const { x, y } = project(t.lat, t.lng);
          const on = selectedSet.has(townKey(t));
          return (
            <circle
              key={townKey(t)}
              cx={x}
              cy={y}
              r={on ? 5 : 4}
              className={
                on
                  ? "cursor-pointer fill-brand-600 stroke-white"
                  : "cursor-pointer fill-white stroke-charcoal/55"
              }
              strokeWidth={1.5}
              onClick={() => toggle(townKey(t))}
            >
              <title>{`${t.city}, ${t.state}`}</title>
            </circle>
          );
        })}
      </svg>
      <p className="mt-1 text-right text-[10px] leading-tight text-charcoal">
        © OpenStreetMap · © CARTO
      </p>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
        <TownColumn heading="Iowa" towns={iowa} selectedSet={selectedSet} onToggle={toggle} />
        <TownColumn
          heading="Illinois"
          towns={illinois}
          selectedSet={selectedSet}
          onToggle={toggle}
        />
      </div>
    </div>
  );
}

function TownColumn({
  heading,
  towns,
  selectedSet,
  onToggle,
}: {
  heading: string;
  towns: ServiceTown[];
  selectedSet: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-charcoal">{heading}</p>
      <ul className="space-y-1">
        {towns.map((t) => {
          const key = townKey(t);
          const on = selectedSet.has(key);
          return (
            <li key={key}>
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => onToggle(key)}
                className={[
                  "flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2 text-left text-sm",
                  on ? "bg-brand-50 text-brand-800" : "text-charcoal hover:bg-field-gray",
                ].join(" ")}
              >
                <span
                  aria-hidden
                  className={[
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                    on ? "border-brand-600 bg-brand-600 text-white" : "border-charcoal/30 bg-white",
                  ].join(" ")}
                >
                  {on ? "✓" : ""}
                </span>
                {t.city}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Distance helper re-exported for callers that want a "nearest town" hint. */
export { haversineMiles };
